import { Tooltip, Typography, Button } from 'antd'
import { ColumnsType } from 'antd/lib/table'

import { GoodNetworkSignalSVG, NoNetworkSignalSVG } from 'assets/svg/network'

import { ChargerConnectorStateBadgeRedesign } from 'components/chargers/charger-connector-state-badge-redesign'
import ServiceStatusBadge from 'components/connectors/ServiceStatusBadge'
import { DateTimeFromNow, DateTimeV2 } from 'components/date-time'
import { AlignedContentLeft } from 'components/wrapper'
import { NewListing } from 'models/listing'

import { useChargerTranslation } from 'hooks/translation/useChargerTranslation'
import { useGeneralTranslation } from 'hooks/translation/useGeneralTranslation'

import HandlePrice from 'features/listings/functions/HandlePrice'

import { MapSVG } from 'assets/svg/map'
import { MapHiddenSVG } from 'assets/svg/mapHidden'
import { renderFormatMessage } from 'helpers/intl'
import { Tenant, TenantRef } from 'models/tenant'
import { useAppState } from 'state'
import { theme } from 'theme'
import ChargerAccess from './ChargerAccess'
import ActionsMenu from './ChargersActionsMenu'
import TogglingBadge from './ToggleConnectivity'
import { ConnectorTypeDisplay } from '../../components/chargers/connector-type-display'

export const ChargersColumns = (
  tenant: TenantRef,
  reloadTable: () => void,
  onConnectivityStatusChange: (connectivityStatus?: string) => void,
  connectivityStatus?: string,
  onOpenConnectivityModal?: (chargerId: number, chargerTitle: string) => void,
): ColumnsType<NewListing> => {
  const { currentUser, IsDesktop } = useAppState()
  const isDesktop = IsDesktop()
  const isAdmin = currentUser?.role === 'admin'

  const { accessText, pricingPlanText } = useGeneralTranslation()
  const { chargingStatusText, serviceStatusText } = useChargerTranslation()
  const { chargerIdText, makeAndModelText, serialNumberText } = useChargerTranslation()


  const renderListingTitle = (listing?: NewListing): JSX.Element | string => {
    if (!listing || !listing.id) {
      return '-'
    }
    return isAdmin ? (
      <div className="flex-and-gap-below" aria-label={`Charger ${listing.title}`}>
        {listing.title || '-'}
        {listing?.displayName && <span className="paragraph-04-light opacity-06">{listing?.displayName}</span>}
        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
          <ConnectorTypeDisplay connectorType={listing.connector?.connectorType} />
          <span className="opacity-06 paragraph-04-light">
            {listing.connector ? `Conn. ID: ${listing.connector?.connectorId}` : null}
          </span>
        </div>
      </div>
    ) : (
      <div className="flex-and-gap-below" aria-label={`Charger ${listing.title}`}>
        {listing.title || '-'}
        {listing?.displayName && <span className="paragraph-04-light opacity-06">{listing?.displayName}</span>}
      </div>
    )
  }

  const isChargerOnline = (listing: NewListing) => {
    if (isAdmin) {
      return (
        <Button
          type="link"
          onClick={() => {
            if (onOpenConnectivityModal && listing.id) {
              onOpenConnectivityModal(listing.id, listing.title)
            }
          }}
        >
          {listing?.connectivity ? (
            <Tooltip title={renderFormatMessage('dashboard.text.online', 'Online')}>{GoodNetworkSignalSVG}</Tooltip>
          ) : (
            <>
              <Tooltip title={renderFormatMessage('dashboard.text.offline', 'Offline')}>
                <span>{NoNetworkSignalSVG}</span>
              </Tooltip>
              <DateTimeFromNow date={listing?.lastPingAt !== undefined ? listing.lastPingAt : listing?.charger?.lastDisconnectedAt} />
            </>
          )}
        </Button>
      )
    }

    return listing?.connectivity ? (
      <Tooltip title={renderFormatMessage('dashboard.text.online', 'Online')}>{GoodNetworkSignalSVG}</Tooltip>
    ) : (
      <>
        <Tooltip title={renderFormatMessage('dashboard.text.offline', 'Offline')}>
          <span>{NoNetworkSignalSVG}</span>
        </Tooltip>
        <DateTimeFromNow date={listing?.lastPingAt !== undefined ? listing.lastPingAt : listing?.charger?.lastDisconnectedAt} />
      </>
    )
  }

  const isChargerHiddenOnMap = (listing: NewListing) => {
    return listing?.hiddenOnMap ? (
      <Tooltip title={renderFormatMessage('dashboard.text.hiddenOnMap', 'Hidden on map')}>
        <span>
          <MapHiddenSVG />
        </span>
      </Tooltip>
    ) : (
      <Tooltip title={renderFormatMessage('dashboard.text.visibleOnMap', 'Visible on map')}>
        <span>
          <MapSVG color={theme.colors.greenBase} />
        </span>
      </Tooltip>
    )
  }

  const renderSerialNumber = (listing: NewListing) => {
    const serialNumber = listing?.charger?.chargePointSerialNumber
    const firmwareVersion = listing?.charger?.firmwareVersion
    return (
      <AlignedContentLeft>
        <Typography.Text
          style={{ width: 150 }}
          ellipsis={{ tooltip: serialNumber }}
          aria-label={serialNumber}
          title={serialNumber}
        >
          {serialNumber}
        </Typography.Text>
        <span className="paragraph-04-light opacity-06">{firmwareVersion}</span>
      </AlignedContentLeft>
    )
  }

  const renderConnectorStatus = (listing: NewListing) => {
    if (!listing?.id || !listing?.connector?.ocppStatus) {
      return null
    }
    return (
      <AlignedContentLeft>
        <ChargerConnectorStateBadgeRedesign state={listing.connector.ocppStatus} />
        {listing?.connector?.lastStatusChangedAt && (
          <DateTimeV2 date={listing.connector.lastStatusChangedAt} inlineDateTime ignoreSeconds />
        )}
      </AlignedContentLeft>
    )
  }

  const columns: ColumnsType<NewListing> = [
    {
      title: (
        <Tooltip
          title={renderFormatMessage(
            'dashboard.text.chargerID.description',
            'Unique ID that can be found on the front of a charger',
          )}
        >
          {chargerIdText}
        </Tooltip>
      ),
      key: 'listing_title',
      render: renderListingTitle,
      width: '9%',
    },
    {
      title: (
        <Tooltip
          title={renderFormatMessage(
            'dashboard.text.serialNumber.description',
            'Manufacturer identifier for each charger, not visible to drivers',
          )}
        >
          {serialNumberText}
        </Tooltip>
      ),
      key: 'serial_number',
      width: '13%',
      render: (listing: NewListing) => renderSerialNumber(listing),
    },
    {
      title: (
        <Tooltip
          title={renderFormatMessage(
            'dashboard.text.makeAndModel.description',
            "Make and model of the charger's manufacturer",
          )}
        >
          {makeAndModelText}
        </Tooltip>
      ),
      key: 'make_and_model',
      width: '11%',
      render: (listing: NewListing) => (
        <div className="flex-and-gap-below">
          <div>{listing?.charger?.chargePointVendor}</div>
          <span className="paragraph-04-light opacity-06">{listing?.charger?.chargePointModel}</span>
          <span className="paragraph-04-light opacity-06">{`${listing?.charger?.chargingType ?? ''}${
            listing?.charger?.deratedWatt
              ? ` - ${parseFloat(`${listing?.charger?.deratedWatt / 1000}`).toFixed(1)} kW`
              : ''
          }`}</span>
        </div>
      ),
    },
    {
      title: (
        <Tooltip title={renderFormatMessage('dashboard.text.pricingPlan.description', 'Pricing plan of the charger')}>
          {pricingPlanText}
        </Tooltip>
      ),
      key: 'pricing_plan',
      width: isDesktop ? '13%' : '15%',
      render: ({ id, ...rest }: NewListing) => (id ? <HandlePrice listing={{ id, ...rest }} /> : null),
    },
    {
      title: (
        <Tooltip title={renderFormatMessage('dashboard.text.access.description', 'Access status of the charger')}>
          {accessText}
        </Tooltip>
      ),
      key: 'access',
      width: isDesktop ? '12%' : '14%',
      render: (listing: NewListing) =>
        !listing?.id ? null : <ChargerAccess listingTitle={listing.title} accessPlans={listing.accessPlans} />,
    },
    {
      title: (
        <Tooltip
          title={renderFormatMessage('dashboard.text.serviceStatus.description', 'Service status of the charger')}
        >
          {serviceStatusText}
        </Tooltip>
      ),
      key: 'service_status',
      width: '10%',
      render: (listing: NewListing) =>
        !listing || !listing.id ? null : listing.connector && listing.connector.serviceStatus ? (
          <ServiceStatusBadge state={listing.connector.serviceStatus} />
        ) : null,
    },
    {
      title: (
        <Tooltip
          title={renderFormatMessage(
            'dashboard.text.connectorStatus.description',
            'The last charging status received from the charger',
          )}
        >
          {chargingStatusText}
        </Tooltip>
      ),
      key: 'connector_status',
      width: isDesktop ? 200 : 150,
      render: (listing: NewListing) => renderConnectorStatus(listing),
    },
    {
      title: (
        <TogglingBadge
          onConnectivityStatusChange={onConnectivityStatusChange}
          defaultConnectivityStatus={connectivityStatus}
        />
      ),
      key: 'connectivity',
      width: isDesktop ? 140 : 70,
      render: (listing: NewListing) => isChargerOnline(listing),
    },
    {
      title: (
        <Tooltip title={renderFormatMessage('dashboard.text.map.description', 'hidden map')}>
          <span>
            <MapSVG />
          </span>
        </Tooltip>
      ),
      key: 'permission',
      width: isDesktop ? 130 : 70,
      render: (listing: NewListing) => isChargerHiddenOnMap(listing),
    },
    {
      title: 'Actions',
      key: 'actions',
      align: 'right',
      render: (listing: NewListing) => (
        <ActionsMenu reloadTable={reloadTable} tenant={tenant as Tenant} listing={listing} />
      ),
    },
  ]

  return columns
}
